import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAppTranslation } from "locale/useAppTranslation";
import { useAuditLogFeed } from "hooks/useAuditLogFeed";
import { displayUserName } from "_helper/actor";
import { groupBy } from "_helper/group";
import { diffRecord } from "_helper/diff";
import { safeParse } from "_helper/json";
import { normalizeKey, titleCase, toSnakeCase } from "_helper/text";
import {
  formatKm,
  isDateOnlyLike,
  isDateTimeLike,
} from "_helper/valueFormat";
import { dateFormatter, historyDateTimeFormatter } from "_helper/timeFormatter";
import type {
  HistoryDataSidebarItem,
  HistoryDataSidebarSummaryItem,
} from "components/shared/history-data-sidebar";
import type { AuditLogItem } from "_interfaces/audit-log.interfaces";

/**
 * Fleet's history hook — fleet owns its own data shapes and formatting.
 *
 * Fleet rows come from the transport-resource audit service:
 * - rows of one request share a request_id → grouped into one entry
 * - snapshots are flat, PascalCase-keyed objects (Go-style)
 * - date-named fields render date-only even when sent with a time part
 * - odometer renders as km; auxiliary arrays render as joined names
 * - redundant ID changes are suppressed when a Name field is present
 *
 * Mechanics (diff, grouping, value primitives) come from _helper;
 * every fleet-specific decision lives here.
 */

const IGNORED_KEYS = [
  "UpdatedAt",
  "UpdatedBy",
  "CreatedAt",
  "CreatedBy",
  "ID",
  "DeletedAt",
  "DeletedBy",
  "CreatedByName",
  "UpdatedByName",
  "fleet_picture_url",
  "FleetPictureURL",
  "Documents",
  "AssociatedAuxiliaryID",
  "VehicleTypeID",
  "VehicleModelID",
  "VendorID",
  "DriverID",
];

const isIgnored = (key: string): boolean =>
  IGNORED_KEYS.some((ik) => normalizeKey(ik) === normalizeKey(key));

interface FleetChange {
  field: string;
  old: string;
  new: string;
}

const formatFleetValue = (value: unknown, field: string): string => {
  if (value === null || value === undefined || value === "") return "—";
  const valueStr = typeof value === "string" ? value.trim() : "";

  if (typeof value === "string") {
    if (isDateOnlyLike(valueStr)) return dateFormatter(valueStr);
    const isDateOnlyField = /expiry|maintenance|date/i.test(field);
    if (isDateOnlyField && isDateTimeLike(valueStr)) return dateFormatter(valueStr);
    if (isDateTimeLike(valueStr)) return historyDateTimeFormatter(valueStr);
  }

  const key = field.toLowerCase();
  if (key === "odometer") return formatKm(value);
  if (key === "associatedauxiliaries") {
    const names = Array.isArray(value)
      ? value.map((item) => item?.Name || item?.name).filter(Boolean)
      : [];
    return names.length > 0 ? names.join(", ") : "—";
  }
  return String(value);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Snapshot diff for one row. Returns fleet-shaped changes:
 * formatted values, equal-after-formatting dropped, redundant IDs
 * suppressed when the corresponding Name field exists in the snapshot.
 */
const snapshotChanges = (
  oldRaw: unknown,
  newRaw: unknown,
  formatValue: (value: unknown, field: string) => string,
): FleetChange[] => {
  const oldVal = typeof oldRaw === "string" ? JSON.parse(oldRaw || "{}") : oldRaw;
  const newVal = typeof newRaw === "string" ? JSON.parse(newRaw || "{}") : newRaw;
  if (!isRecord(oldVal) && !isRecord(newVal)) return [];

  const oldRecord = isRecord(oldVal) ? oldVal : {};
  const newRecord = isRecord(newVal) ? newVal : {};

  // Full present-key set (changed or not) — ID suppression keys off it.
  const presentKeys = new Set(
    [...Object.keys(oldRecord), ...Object.keys(newRecord)].filter(
      (key) => !isIgnored(key),
    ),
  );

  const diffs = diffRecord(oldRecord, newRecord, { skip: isIgnored });

  const changes: FleetChange[] = [];
  for (const diff of diffs) {
    // Skip legacy exact-match IDs first...
    if (/id$/i.test(diff.key) && presentKeys.has(diff.key.replace(/id$/i, "Name"))) {
      continue;
    }
    // ...then the general case: an id(s) key whose normalized companion
    // (base + "name[s]") exists in the snapshot means the row carries the
    // names — the UUIDs add nothing. Covers snake_case variants like
    // associated_auxiliary_ids ↔ associated_auxiliary_names.
    if (/ids?$/i.test(diff.key)) {
      const base = diff.key
        .toLowerCase()
        .replace(/[\s_-]+/g, "")
        .replace(/ids?$/, "");
      const named = Array.from(presentKeys).some((presentKey) => {
        const normalized = presentKey.toLowerCase().replace(/[\s_-]+/g, "");
        return normalized === base + "name" || normalized === base + "names";
      });
      if (named) continue;
    }
    const oldFormatted = formatValue(diff.before, diff.key);
    const newFormatted = formatValue(diff.after, diff.key);
    if (oldFormatted !== newFormatted) {
      changes.push({ field: diff.key, old: oldFormatted, new: newFormatted });
    }
  }
  return changes;
};

/**
 * Names for the co-located auxiliary ids: the snapshot carries both
 * `associated_auxiliary_ids` and `associated_auxiliary_names`, so the
 * client resolves names from the same row instead of a second fetch.
 */
const namesFromSnapshot = (raw: unknown): string => {
  const parsed = typeof raw === "string" ? safeParse(raw) : raw;
  if (!isRecord(parsed)) return "—";
  const names = parsed["associated_auxiliary_names"];
  const list = Array.isArray(names) ? names.map(String).filter(Boolean) : [];
  return list.length > 0 ? list.join(", ") : "—";
};

/** A row whose field_changed holds a JSON array is a snapshot row. */
// --- action vocabulary (fleet service contract: CREATED/UPDATED/DELETED
//     via tms.events, plus legacy CREATE/UPDATE/STATUS_UPDATED rows) ---

const normalizeActionType = (action?: string): string => {
  if (!action) return "UPDATE";
  const upper = action.toUpperCase();
  if (upper.includes("CREATE") || upper.includes("IMPORT")) return "CREATE";
  if (upper.includes("DELETE") || upper.includes("REMOVE")) return "DELETE";
  if (
    upper.includes("STATUS") ||
    upper.includes("ACTIVAT") ||
    upper.includes("DEACTIVAT") ||
    upper.includes("PUBLISH")
  ) {
    return "STATUS_UPDATED";
  }
  return "UPDATE";
};

/** Label lookup that can't leak raw keys: snake-key hit, else title-case. */
const makeResolveLabel = (
  t: (key: never, options?: Record<string, unknown>) => string,
) => {
  return (rawKey: string): string => {
    const snake = toSnakeCase(rawKey);
    const hit = t(snake as never);
    return hit && hit !== snake ? hit : titleCase(rawKey);
  };
};

const isSnapshotRow = (log: AuditLogItem): boolean => {
  if (!log.field_changed) return true;
  try {
    return Array.isArray(JSON.parse(log.field_changed));
  } catch {
    return false;
  }
};

const rowChanges = (log: AuditLogItem): FleetChange[] => {
  // New backend: field_changes carries clean per-field diffs — prefer it
  // and apply the same value formatting as the snapshot path. System
  // fields (actor/timestamp entries) are skipped.
  if (log.field_changes && log.field_changes.length > 0) {
    return log.field_changes
      .filter((change) => !isIgnored(change.field))
      .map((change) => {
        // Aux ids render as names, resolved from the co-located snapshot.
        if (toSnakeCase(change.field) === "associated_auxiliary_ids") {
          return {
            field: change.field,
            old: namesFromSnapshot(log.old_value),
            new: namesFromSnapshot(log.new_value),
          };
        }
        return {
          field: change.field,
          old: formatFleetValue(safeParse(change.old_value), change.field),
          new: formatFleetValue(safeParse(change.new_value), change.field),
        };
      });
  }
  // Legacy rows: single-field word, or snapshot pair.
  if (log.field_changed && !isSnapshotRow(log)) {
    return [{ field: log.field_changed, old: log.old_value, new: log.new_value }];
  }
  return snapshotChanges(log.old_value, log.new_value, formatFleetValue);
};

export const useFleetHistory = (id: string, isOpen: boolean, detail?: any) => {
  const { t: tHistory } = useAppTranslation("historyDataSidebar");
  const { i18n } = useTranslation();
  const [searchValue, setSearchValue] = useState("");

  const feed = useAuditLogFeed({
    entityType: "FLEET",
    entityId: id,
    isOpen,
    pageSize: 10,
  });

  const sidebarItems: HistoryDataSidebarItem[] = useMemo(() => {
    if (!feed.logs.length) return [];

    // Group by request; without one, fall back to timestamp + action so
    // distinct same-second operations (e.g. a status flip and a delete)
    // never fuse into one entry.
    const groups = groupBy(
      feed.logs,
      (log) =>
        log.request_id ||
        (log.changed_at ? `${log.changed_at}::${log.action}` : log.id),
    );

    const resolveLabel = makeResolveLabel(tHistory);

    const items = groups.map(({ items: logs }) => {
      const first = logs[0];
      const isCreate = normalizeActionType(first.action) === "CREATE";
      const isDelete = normalizeActionType(first.action) === "DELETE";
      // A delete is one line — never diff the old snapshot into removals.
      const changes = isDelete ? [] : logs.flatMap(rowChanges);
      const actionKey = isCreate ? "action-create" : "action-edit";

      let description: ReactNode = null;
      if (changes.length > 0) {
        description = (
          <ul className="list-disc pl-4 space-y-1">
            {changes.map((change, idx) => (
              <li key={idx} className="text-slate-500">
                {tHistory(isCreate ? "create-text" : "change-text", {
                  field: resolveLabel(change.field),
                  old: change.old || "—",
                  new: change.new || "—",
                })}
              </li>
            ))}
          </ul>
        );
      } else if (isDelete) {
        description = (
          <ul className="list-disc pl-4 space-y-1">
            <li className="text-slate-500">{tHistory("data-deleted")}</li>
          </ul>
        );
      } else if (isCreate) {
        description = (
          <ul className="list-disc pl-4 space-y-1">
            <li className="text-slate-500">{tHistory("data-created")}</li>
          </ul>
        );
      }

      return {
        id: first.id,
        timestampLabel: first.changed_at ? historyDateTimeFormatter(first.changed_at) : "—",
        rawTimestamp: first.changed_at,
        actorName: displayUserName(first.changed_by_name),
        actionLabel: tHistory(actionKey as never),
        description,
      } satisfies HistoryDataSidebarItem;
    });

    // Suppress bookkeeping rows (updated_at-only, etc.) whose changes
    // are entirely system fields — they render as bare dots. Non-create
    // groups with no changes build a null description.
    const visible = items.filter((item) => Boolean(item.description));

    if (!searchValue) return visible;

    const lowerSearch = searchValue.toLowerCase();
    return items.filter(
      (item) =>
        (item.actorName ?? "").toLowerCase().includes(lowerSearch) ||
        (item.actionLabel ?? "").toLowerCase().includes(lowerSearch),
    );
  }, [feed.logs, tHistory, i18n.language, searchValue]);

  const summaryItems: HistoryDataSidebarSummaryItem[] = useMemo(() => {
    const items: HistoryDataSidebarSummaryItem[] = [];

    const creatorName = detail?.created_by
      ? displayUserName(detail.created_by)
      : "—";
    const createdAtStr = detail?.created_at
      ? historyDateTimeFormatter(detail.created_at)
      : "—";

    const rawUpdaterName = detail?.updated_by || detail?.created_by;
    const updaterName = rawUpdaterName ? displayUserName(rawUpdaterName) : "—";
    const updatedAtStr = detail?.updated_at
      ? historyDateTimeFormatter(detail.updated_at)
      : detail?.created_at
        ? historyDateTimeFormatter(detail.created_at)
        : "—";

    if (detail?.created_by || detail?.created_at) {
      items.push({
        id: "created",
        label: tHistory("created-by") || "Created by",
        value: `${creatorName}, ${createdAtStr}`,
      });
    }

    if (detail?.updated_by || detail?.updated_at || detail?.created_by) {
      items.push({
        id: "updated",
        label: tHistory("last-update-by") || "Last Update by",
        value: `${updaterName}, ${updatedAtStr}`,
      });
    }

    return items;
  }, [tHistory, detail]);

  return {
    sidebarItems,
    summaryItems,
    isLoading: feed.state.isLoading,
    isFetching: feed.state.isFetching,
    isLoadingMore: feed.state.isLoadingMore,
    hasMore: feed.state.hasMore,
    total: feed.state.total,
    loadMore: feed.loadMore,
    startDate: feed.fromDateValue,
    endDate: feed.toDateValue,
    setDateRange: feed.setDateRange,
    searchValue,
    setSearchValue,
    state: feed.state,
  };
};
